import { type ReactNode, createContext, useContext, useState } from 'react';
import { deleteValue as deleteValueRequest, getAllValuesV2 } from '../../../sources/value';
import type { Filter, OrderDirection, Paging } from '../../../types/api';
import { ElementType } from '../../../types/element';
import type { ValueElementResponseAPI } from '../../../types/value';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';

interface Pagination extends Paging {
    search: string;
    orderBy: string;
    orderDirection: OrderDirection;
}

interface Context {
    currentScreen: string;
    fetchValues: (filters: Omit<Filter, 'limit'>) => Promise<void>;
    values: ValueElementResponseAPI[];
    valueToEdit: ValueElementResponseAPI;
    editValue: (value: ValueElementResponseAPI) => void;
    returnToValueList: () => void;
    createValue: () => void;
    valuesLoading: boolean;
    valueToDelete: ValueElementResponseAPI | null;
    setValueToDelete: (value: ValueElementResponseAPI) => void;
    deleteValue: () => Promise<void>;
    paging: Pagination;
}

interface Props {
    children: ReactNode;
}

const Context = createContext<Context | undefined>(undefined);

const ValuesProvider = ({ children }: Props) => {
    const VALUE_SCREENS = {
        valueList: 'valueList',
        valueDetail: 'valueDetail',
    };

    const initialValueToEdit: ValueElementResponseAPI = {
        id: '',
        access: 'PRIVATE',
        contentFormat: '',
        contentType: null,
        defaultContentValue: null,
        defaultObjectData: [],
        elementType: ElementType.Value,
        developerName: '',
        developerSummary: '',
        initializationOperations: [],
        isEncrypted: false,
        typeElementId: null,
        typeElementDeveloperName: null,
        updateByName: false,
        isSecret: false,
        isVersionless: false,
        dateCreated: '',
        dateModified: '',
        whoCreated: null,
        whoModified: null,
        whoOwner: null,
        hasValue: false,
    };

    const [currentScreen, setCurrentScreen] = useState(VALUE_SCREENS.valueList);
    const [values, setValues] = useState<ValueElementResponseAPI[]>([]);
    const [valuesLoading, setValuesLoading] = useState(true);
    const [valueToEdit, setValueToEdit] = useState<ValueElementResponseAPI>(initialValueToEdit);
    const [valueToDelete, setValueToDelete] = useState<ValueElementResponseAPI | null>(null);

    const dispatchNotification = useNotificationDispatch();

    const [paging, updatePaging] = useState<Pagination>({
        page: 1,
        pageSize: 20,
        total: 0,
        search: '',
        orderBy: 'dateModified',
        orderDirection: 'DESC',
    });

    const fetchValues = async (filters: Filter) => {
        setValuesLoading(true);

        try {
            const { items, _meta } = await getAllValuesV2({
                ...filters,
                // Endpoint doesn't accept page size, so map it to limit
                limit: filters.pageSize ?? paging.pageSize,
            });

            setValues(items);

            const { search, orderBy, orderDirection } = filters;

            updatePaging({
                ..._meta,
                search: search ?? '',
                orderBy: orderBy ?? '',
                orderDirection: orderDirection ?? 'ASC',
            });
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        } finally {
            setValuesLoading(false);
        }
    };

    const deleteValue = async () => {
        const { id, developerName } = valueToDelete as ValueElementResponseAPI;

        try {
            await deleteValueRequest(id);

            const remainingValues = values.filter((value) => value.id !== id);

            setValues(remainingValues);
            returnToValueList();

            dispatchNotification({
                type: 'notifySuccess',
                payload: {
                    message: `Value: ${developerName} has been successfully deleted.`,
                },
            });
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        }
    };

    const editValue = (value: ValueElementResponseAPI) => {
        setCurrentScreen(VALUE_SCREENS.valueDetail);
        setValueToEdit(value);
    };

    const createValue = () => {
        setCurrentScreen(VALUE_SCREENS.valueDetail);
        setValueToEdit(initialValueToEdit);
    };

    const returnToValueList = () => {
        setCurrentScreen(VALUE_SCREENS.valueList);
    };

    const contextValue: Context = {
        currentScreen,
        fetchValues,
        values,
        valueToEdit,
        editValue,
        returnToValueList,
        createValue,
        valuesLoading,
        valueToDelete,
        setValueToDelete,
        deleteValue,
        paging,
    };

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

const useValues = () => {
    const context = useContext(Context);
    if (context === undefined) {
        throw new Error('useValues must be used within a ValuesProvider');
    }
    return context;
};

export { ValuesProvider, useValues };
